import {
  AIUsageFeature,
  AIUsageRequestPayload,
  AIUsageReservation,
  AIUsageSettings,
  AIUsageStatus,
  AIUserLimitInfo
} from '../../types/ai'
import { normalizeVatliUrl } from '../../utils/urlNormalizer'
import { API_CONFIG } from '../../config/api.config'

const API_BASE_URL = API_CONFIG.python.apiUrl
const STATUS_TTL = 30 * 1000 // 30s cache

interface CachedStatus {
  status: AIUsageStatus
  fetchedAt: number
}

class AIUsageService {
  private statusCache = new Map<string, CachedStatus>()

  private buildHeaders(includeAuth = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }

    if (includeAuth) {
      const token = localStorage.getItem('auth_token')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    return headers
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    opts: { requiresAuth?: boolean } = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${path}`
    const headers = {
      ...this.buildHeaders(opts.requiresAuth),
      ...(options.headers || {})
    }

    const response = await fetch(url, {
      ...options,
      headers
    })

    let responseBody: any = null

    try {
      responseBody = await response.clone().json()
    } catch {
      responseBody = null
    }

    if (!response.ok) {
      const message =
        responseBody?.message ||
        responseBody?.error ||
        `Yêu cầu thất bại với mã lỗi ${response.status}`
      const err = new Error(message)
        ; (err as any).status = response.status
        ; (err as any).code = responseBody?.code
      throw err
    }

    return responseBody as T
  }

  async getStatus(userId: string, force = false): Promise<AIUsageStatus> {
    if (!userId) {
      throw new Error('Không xác định được tài khoản người dùng.')
    }

    const cached = this.statusCache.get(userId)
    if (!force && cached && Date.now() - cached.fetchedAt < STATUS_TTL) {
      return cached.status
    }

    const status = await this.request<AIUsageStatus>(
      `/ai-usage/status?userId=${encodeURIComponent(userId)}`
    )
    this.statusCache.set(userId, { status, fetchedAt: Date.now() })
    return status
  }

  async consume(payload: AIUsageRequestPayload): Promise<AIUsageReservation> {
    if (!payload.userId) {
      throw new Error('Không xác định được tài khoản người dùng.')
    }

    const reservation = await this.request<AIUsageStatus & { usageId: string }>(
      '/ai-usage/consume',
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    )

    this.statusCache.set(payload.userId, {
      status: {
        userId: reservation.userId,
        usageDate: reservation.usageDate,
        dailyLimit: reservation.dailyLimit,
        used: reservation.used,
        remaining: reservation.remaining,
        featureBreakdown: reservation.featureBreakdown,
        nextResetAt: reservation.nextResetAt,
        lastUpdatedAt: reservation.lastUpdatedAt,
        serverTime: reservation.serverTime
      },
      fetchedAt: Date.now()
    })

    return {
      usageId: reservation.usageId,
      dailyLimit: reservation.dailyLimit,
      used: reservation.used,
      remaining: reservation.remaining,
      featureBreakdown: reservation.featureBreakdown,
      nextResetAt: reservation.nextResetAt
    }
  }

  async revert(usageId?: string, userId?: string): Promise<void> {
    if (!usageId) return

    await this.request('/ai-usage/revert', {
      method: 'POST',
      body: JSON.stringify({ usageId })
    })

    if (userId) {
      await this.getStatus(userId, true)
    }
  }

  async getSettings(): Promise<AIUsageSettings> {
    return this.request<AIUsageSettings>('/ai-usage/settings', {}, { requiresAuth: true })
  }

  async updateSettings(dailyLimit: number): Promise<AIUsageSettings> {
    return this.request<AIUsageSettings>(
      '/ai-usage/settings',
      {
        method: 'POST',
        body: JSON.stringify({ dailyLimit })
      },
      { requiresAuth: true }
    )
  }

  async getUserLimit(userId: string): Promise<AIUserLimitInfo> {
    return this.request<AIUserLimitInfo>(
      `/ai-usage/user-limit?userId=${encodeURIComponent(userId)}`,
      {},
      { requiresAuth: true }
    )
  }

  async setUserLimit(userId: string, dailyLimit: number, notes?: string): Promise<AIUserLimitInfo> {
    return this.request<AIUserLimitInfo>(
      '/ai-usage/user-limit',
      {
        method: 'POST',
        body: JSON.stringify({ userId, dailyLimit, notes })
      },
      { requiresAuth: true }
    )
  }

  async deleteUserLimit(userId: string): Promise<AIUserLimitInfo> {
    return this.request<AIUserLimitInfo>(
      `/ai-usage/user-limit?userId=${encodeURIComponent(userId)}`,
      {
        method: 'DELETE'
      },
      { requiresAuth: true }
    )
  }

  clearCache(userId?: string) {
    if (userId) {
      this.statusCache.delete(userId)
    } else {
      this.statusCache.clear()
    }
  }

  mapContentTypeToFeature(contentType: string): AIUsageFeature {
    switch (contentType) {
      case 'quiz':
        return 'lesson_quiz'
      case 'formula':
        return 'lesson_formula'
      case 'exercise':
        return 'interactive_question'
      default:
        return 'lesson_theory'
    }
  }
}

export const aiUsageService = new AIUsageService()
export default aiUsageService

